// Keeps client, vendor, job and business names away from the AI (EULA section 8.2).
//
// Before anything goes to the AI, known names are swapped for neutral labels:
// clients -> "Client 1", vendors -> "Vendor 1", jobs -> "Job 1", the business -> "the business".
// The AI's answer is turned back into real names before the owner sees it.
//
// The mapping lives only in the object createPrivacy() returns. It is never sent anywhere,
// stored or logged. A new chat makes a new object. Pure code: no React, no network.
//
// Replacement rules (pseudonymize):
//  - Every known name is found anywhere in the text, ignoring upper/lower case.
//  - Longest names win, so "Greene primary bath" is replaced before "Greene".
//  - A name only matches as a whole word: no letter, digit or underscore may touch either end.
//    So a client called "Al" does not change "Alan", and "Home" does not change "Homestead".
//  - Spaces match any run of spaces; ' and the curly apostrophe are the same; "&" and "and" are the same
//    ("Smith & Sons" also finds "Smith and Sons"); a final "." is optional ("Acme Inc." finds "Acme Inc").
//  - A client named "<First> <Last>" (or "<First> <M.> <Last>") also gets its surname replaced on its own,
//    and so does "<Last> Family", "<Last> Household" and "<Last> Residence". The surname is skipped when:
//    it is under 3 letters; the name has a business word in it (LLC, Plumbing, Construction...) or a digit;
//    or two different clients share the surname (we cannot tell which one is meant, so the full names are
//    still replaced but the bare surname is left alone). First names alone are never replaced.
//  - A name that is one common word (a vendor called "Home", a surname like "Brown") only matches when it is
//    written exactly as it is stored, so the ordinary word "home" is left alone.
//  - Empty, one-character and app-made names ("Unknown vendor", "No client") are ignored.
//  - The same name always gets the same label, in this and later messages of the chat.
//  - Names are replaced in one pass, so a label that has just been put in is never replaced again.
//
// Restoring (restore): "Client 1", "Vendor 2", "Job 3" go back to the real names. A label the chat never
// handed out is left as it is. "the business" is NOT turned back, because the AI also says it in
// ordinary sentences; the owner just reads "the business".

const LETTER_OR_DIGIT = "\\p{L}\\p{N}_";

// Words that are normal English as well as possible names. Written differently from how they are stored,
// they are left alone.
const COMMON_WORDS = new Set([
  "home", "house", "the", "and", "job", "jobs", "client", "clients", "vendor", "vendors", "business",
  "new", "old", "main", "general", "supply", "supplies", "materials", "material", "labor", "tools", "parts",
  "service", "services", "group", "company", "project", "projects", "repair", "repairs", "kitchen", "bath",
  "bathroom", "paint", "lumber", "concrete", "electric", "plumbing", "roofing", "city", "county", "state",
  "green", "brown", "white", "black", "gray", "grey", "baker", "cook", "miller", "carpenter", "mason",
  "hunter", "fisher", "king", "young", "long", "short", "hill", "field", "park", "price", "rich", "sharp",
]);

// A name with one of these words is a company or place, not a person, so no surname is pulled out of it.
const BUSINESS_WORDS = new Set([
  "llc", "inc", "co", "corp", "company", "construction", "services", "service", "group", "homes", "builders",
  "building", "plumbing", "electric", "electrical", "roofing", "landscaping", "design", "designs", "studio",
  "studios", "photography", "consulting", "associates", "partners", "enterprises", "properties", "realty",
  "holdings", "solutions", "supply", "contracting", "contractors", "remodeling", "renovations", "restoration",
  "hvac", "trust", "church", "school", "hospital", "bank", "city", "county", "department", "association",
  "club", "hotel", "restaurant", "cafe", "market", "store", "shop", "depot", "hardware", "lumber", "paint",
]);

// Names the app makes up itself when it has none. They are not real names.
const NOT_NAMES = new Set(["unknown", "unknown vendor", "no client", "unnamed job", "unnamed", "n/a", "na", "none"]);

const FAMILY_WORDS = new Set(["family", "household", "residence"]);
const SUFFIXES = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv"]);

export const PLACEHOLDER_INSTRUCTION =
  'Names in this data are replaced with labels: "Client 1", "Vendor 1", "Job 1" and "the business". ' +
  "Use the labels exactly as written (for example Client 2), never guess or invent a real name, and never join two labels together.";

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The comparison form of a name: lower case, one space between words, one kind of apostrophe, "&" for "and".
function keyOf(name) {
  return String(name)
    .toLowerCase()
    .replace(/[‘’ʼ]/g, "'")
    .replace(/\s*&\s*|\s+and\s+/g, " & ")
    .replace(/\s+/g, " ")
    .trim();
}

// The text pattern for a key (see keyOf).
function patternOf(key) {
  const optionalDot = key.endsWith(".");
  const core = optionalDot ? key.slice(0, -1) : key;
  const body = core
    .split(" & ")
    .map((part) => part.split(" ").map((w) => escapeRe(w).replace(/'/g, "['\\u2019\\u2018\\u02bc]")).join("\\s+"))
    .join("(?:\\s*&\\s*|\\s+and\\s+)");
  return body + (optionalDot ? "\\.?" : "");
}

const countLettersAndDigits = (s) => (s.match(/[\p{L}\p{N}]/gu) || []).length;

export function createPrivacy() {
  const entries = new Map();        // key -> { key, text, label, exact }   exact: only an identical spelling matches
  const labels = new Map();         // "Client 1" -> real name (first spelling seen)
  const counts = { Client: 0, Vendor: 0, Job: 0 };
  const surnameOwners = new Map();  // surname key -> Set of client keys that have it
  const ambiguous = new Set();      // surname keys shared by different clients
  let business = null;              // { name }
  let regex = null;                 // rebuilt after the entries change

  const isUsable = (name) =>
    typeof name === "string" && /\p{L}/u.test(name) && countLettersAndDigits(name) >= 2 && !NOT_NAMES.has(keyOf(name));

  function labelFor(kind, text) {
    counts[kind] += 1;
    const label = `${kind} ${counts[kind]}`;
    labels.set(label, text.trim().replace(/\s+/g, " "));
    return label;
  }

  function addFull(kind, rawName) {
    if (!isUsable(rawName)) return null;
    const text = rawName.trim().replace(/\s+/g, " ");
    const key = keyOf(text);
    const found = entries.get(key);
    if (found && !found.isSurname) return found.label;
    const label = found ? found.label : kind === "Business" ? "the business" : labelFor(kind, text);
    const entry = { key, text, label, exact: isCommonSingleWord(text), isSurname: false };
    entries.set(key, entry);
    regex = null;
    return label;
  }

  const isCommonSingleWord = (text) => !/\s/.test(text.trim()) && COMMON_WORDS.has(keyOf(text));

  // "John Smith", "John Q. Smith", "Smith Family" -> the surname, else null.
  function surnameOf(name) {
    if (/\d/.test(name)) return null;
    let tokens = name.trim().replace(/^the\s+/i, "").split(/\s+/);
    while (tokens.length > 2 && SUFFIXES.has(tokens[tokens.length - 1].toLowerCase())) tokens = tokens.slice(0, -1);
    if (tokens.some((t) => BUSINESS_WORDS.has(t.toLowerCase().replace(/[.,]/g, "")))) return null;
    const nameLike = /^\p{Lu}[\p{L}'’-]*\.?$/u;
    let surname = null;
    if (tokens.length === 2 && FAMILY_WORDS.has(tokens[1].toLowerCase())) {
      if (nameLike.test(tokens[0])) surname = tokens[0];
    } else if (tokens.length === 2 && tokens.every((t) => nameLike.test(t))) {
      surname = tokens[1];
    } else if (tokens.length === 3 && /^\p{Lu}\.?$/u.test(tokens[1]) && nameLike.test(tokens[0]) && nameLike.test(tokens[2])) {
      surname = tokens[2];
    }
    if (!surname) return null;
    surname = surname.replace(/[.,]+$/, "");
    return countLettersAndDigits(surname) >= 3 ? surname : null;
  }

  function addSurname(clientKey, clientName, label) {
    const surname = surnameOf(clientName);
    if (!surname) return;
    const key = keyOf(surname);
    const owners = surnameOwners.get(key) || new Set();
    owners.add(clientKey);
    surnameOwners.set(key, owners);
    if (owners.size > 1) {
      ambiguous.add(key);
      const e = entries.get(key);
      if (e && e.isSurname) { entries.delete(key); regex = null; }
      return;
    }
    if (ambiguous.has(key) || entries.has(key)) return;
    entries.set(key, { key, text: surname, label, exact: isCommonSingleWord(surname), isSurname: true });
    regex = null;
  }

  // names: { clients: [], vendors: [], jobs: [], business: "" }. Safe to call again with more names.
  function register(names = {}) {
    const list = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
    if (isUsable(names.business) && !business) {
      business = { name: names.business.trim() };
      addFull("Business", names.business);
    }
    const clientLabels = [];
    list(names.clients).forEach((n) => {
      const label = addFull("Client", n);
      if (label) clientLabels.push([n, label]);
    });
    list(names.vendors).forEach((n) => addFull("Vendor", n));
    list(names.jobs).forEach((n) => addFull("Job", n));
    // Surnames last, so a full name that is spelled the same always wins.
    clientLabels.forEach(([n, label]) => addSurname(keyOf(n.trim()), n, label));
  }

  function buildRegex() {
    const sorted = [...entries.values()].sort((a, b) => b.key.length - a.key.length || (a.key < b.key ? -1 : 1));
    if (sorted.length === 0) return null;
    const alt = sorted.map((e) => patternOf(e.key)).join("|");
    return new RegExp(`(^|[^${LETTER_OR_DIGIT}])(${alt})(?![${LETTER_OR_DIGIT}])`, "giu");
  }

  function pseudonymize(text) {
    if (typeof text !== "string" || text === "" || entries.size === 0) return text;
    if (!regex) regex = buildRegex();
    if (!regex) return text;
    regex.lastIndex = 0;
    return text.replace(regex, (whole, before, matched) => {
      const key = keyOf(matched);
      const entry = entries.get(key) || entries.get(key.replace(/\.$/, "")) || entries.get(key + ".");
      if (!entry) return whole;
      if (entry.exact && matched !== entry.text) return whole;
      return before + entry.label;
    });
  }

  // Same, for text with structure (objects and lists): every string inside is changed, keys are not.
  function pseudonymizeDeep(value) {
    if (typeof value === "string") return pseudonymize(value);
    if (Array.isArray(value)) return value.map(pseudonymizeDeep);
    if (value && typeof value === "object") {
      const out = {};
      for (const k of Object.keys(value)) out[k] = pseudonymizeDeep(value[k]);
      return out;
    }
    return value;
  }

  const LABEL_RE = new RegExp(`(^|[^${LETTER_OR_DIGIT}])(Client|Vendor|Job) (\\d+)(?![${LETTER_OR_DIGIT}])`, "gu");

  function restoreWith(text, transform) {
    return text.replace(LABEL_RE, (whole, before, kind, n) => {
      const real = labels.get(`${kind} ${n}`);
      return real === undefined ? whole : before + transform(real);
    });
  }

  // Puts real names back into the AI's answer. Inside a chart block (one line of JSON) names are written
  // the way JSON needs them, so a quote mark in a name cannot break the chart.
  function restore(text) {
    if (typeof text !== "string" || text === "" || labels.size === 0) return text;
    const parts = text.split(/(```chart[^\n]*\n[\s\S]*?```)/);
    return parts
      .map((p, i) => (i % 2 === 1 ? restoreWith(p, (real) => JSON.stringify(real).slice(1, -1)) : restoreWith(p, (real) => real)))
      .join("");
  }

  return { register, pseudonymize, pseudonymizeDeep, restore };
}

// Collects every name the app knows that could end up in an AI request.
// jobs: job summaries (name, clientName, purchases[].EntityRef.name, costByVendor keys).
// needsReview: expenses waiting to be assigned (vendor).
export function collectNames({ jobs, needsReview, businessName } = {}) {
  const clients = [];
  const vendors = [];
  const jobNames = [];
  (Array.isArray(jobs) ? jobs : []).forEach((j) => {
    if (!j || typeof j !== "object") return;
    if (j.clientName) clients.push(j.clientName);
    if (j.name) jobNames.push(j.name);
    (Array.isArray(j.purchases) ? j.purchases : []).forEach((p) => { if (p?.EntityRef?.name) vendors.push(p.EntityRef.name); });
    if (j.costByVendor && typeof j.costByVendor === "object") vendors.push(...Object.keys(j.costByVendor));
  });
  (Array.isArray(needsReview) ? needsReview : []).forEach((it) => { if (it?.vendor) vendors.push(it.vendor); });
  return { clients, vendors, jobs: jobNames, business: businessName || "" };
}
